import { useCallback, useMemo, useState } from 'react';
import {
  ANNOTATION_KIND_CODER_SPAN,
  type SourceRange
} from './coder-span-types';
import type { ViewAnnotation } from './cmx-document-content-adapter';

/**
 * Holds a coder's spans for one document body.
 *
 * LOCAL STATE, DELIBERATELY. cqa-service already serves `POST /spans`,
 * `PUT /spans/{id}` and `DELETE /spans/{id}`, and the interaction model around
 * them is production code. What it cannot yet do is anchor a span to a CMX Document
 * Content: it anchors on a legacy `t_document.id` plus `document_version`,
 * which addresses raw rendition bytes rather than a body. Offsets created here
 * are Markdown-body offsets, so persisting them against that anchor would
 * store coordinates for the wrong text.
 *
 * So spans live in component state, behind this seam. When cqa-service accepts
 * `(documentId, layer)`, `createSpan` and `removeSpan` become calls to
 * `enrichingAnnotationHooks.addEmptySpan` / `deleteSpan` and nothing above this
 * hook changes. A coder span must anchor at layer 2 or above, never layer 1:
 * the flat form is derived and its bytes move when the body is re-tiered
 * (CMXDC-LAYER-010).
 */

export type CoderSpan = {
  id: string;
  start: number;
  end: number;
  /** The body text the span covers, kept for display and for debugging. */
  coveredText: string;
};

export type CoderSpanStore = {
  spans: CoderSpan[];
  /** Coder spans in the shape the renderer highlights. */
  annotations: ViewAnnotation[];
  createSpan: (range: SourceRange, coveredText: string) => CoderSpan | null;
  removeSpan: (id: string) => void;
  clearSpans: () => void;
};

let sequence = 0;

function nextSpanId(): string {
  sequence += 1;
  return `coder-span-${sequence}`;
}

/** Resets the id sequence. Test seam only. */
export function resetCoderSpanIds(): void {
  sequence = 0;
}

export function useCoderSpans(initial: CoderSpan[] = []): CoderSpanStore {
  const [spans, setSpans] = useState<CoderSpan[]>(initial);

  const createSpan = useCallback(
    (range: SourceRange, coveredText: string): CoderSpan | null => {
      if (range.end <= range.start) {
        return null;
      }

      const span: CoderSpan = {
        id: nextSpanId(),
        start: range.start,
        end: range.end,
        coveredText
      };

      setSpans((previous) => {
        // Re-selecting the same range is a no-op rather than a duplicate: two
        // spans over identical offsets are indistinguishable to the coder.
        const exists = previous.some(
          (candidate) =>
            candidate.start === span.start && candidate.end === span.end
        );
        return exists ? previous : [...previous, span];
      });

      return span;
    },
    []
  );

  const removeSpan = useCallback((id: string) => {
    setSpans((previous) => previous.filter((span) => span.id !== id));
  }, []);

  const clearSpans = useCallback(() => setSpans([]), []);

  const annotations = useMemo<ViewAnnotation[]>(
    () =>
      spans.map((span) => ({
        id: span.id,
        kind: ANNOTATION_KIND_CODER_SPAN,
        parentId: null,
        spans: [{ start: span.start, end: span.end }],
        attributes: [{ key: 'kind', value: ANNOTATION_KIND_CODER_SPAN }]
      })),
    [spans]
  );

  return { spans, annotations, createSpan, removeSpan, clearSpans };
}
